import './ContactsApp.scss';
import './renderers';
import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import {
  AddressBook,
  MagnifyingGlass,
  Plus,
  ArrowsCounterClockwise,
  UserPlus,
  CheckCircle,
  ArrowLeft,
  DotsThree,
  PencilSimple,
  X,
} from '@phosphor-icons/react';
import { usePageTitle } from '../../hooks/useDocumentTitle';
import useSpaceStore from '../../stores/spaceStore';
import { useContactsAppStore } from './store/contactsAppStore';
import { ContactList } from './components/ContactList/ContactList';
import { ContactDetail } from './components/ContactDetail/ContactDetail';
import { ContactForm } from './components/ContactForm/ContactForm';
import { ContactsEmailWall } from './components/ContactsEmailWall/ContactsEmailWall';
import { SyncModal } from './components/SyncModal/SyncModal';
import { ContactsView } from './types/state';
import { TIER_LABELS } from './types/entity';
import { useDeviceDetection } from '../../components/Space/layouts/FocusedLayout/hooks/useDeviceDetection';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('ContactsApp');

export interface ContactsAppProps {
  windowId: string;
  spaceId: string;
  onClose?: () => void;
}

interface ViewItem { id: ContactsView; label: string }
interface ViewSection { label: string; views: ViewItem[] }

// const VIEW_SECTIONS: ViewSection[] = [
//   {
//     label: 'Relationship',
//     views: [
//       { id: 'all',          label: 'All Contacts' },
//       { id: 'inner_circle', label: TIER_LABELS.inner_circle },
//       { id: 'tier1',        label: TIER_LABELS.tier1 },
//       { id: 'tier2',        label: TIER_LABELS.tier2 },
//       { id: 'tier3',        label: TIER_LABELS.tier3 },
//       { id: 'dormant',      label: TIER_LABELS.dormant },
//     ],
//   },
//   {
//     label: 'Signals',
//     views: [
//       { id: 'stale', label: 'Overdue' },
//       { id: 'hot',   label: '🔥 Hot' },
//     ],
//   },
//   {
//     label: 'Sources',
//     views: [
//       { id: 'gmail',           label: 'Gmail' },
//       { id: 'calendar',        label: 'Calendar' },
//       { id: 'google_contacts', label: 'Google Contacts' },
//       { id: 'linkedin',        label: 'LinkedIn' },
//       { id: 'reddit',          label: 'Reddit' },
//       { id: 'twitter',         label: 'Twitter / X' },
//       { id: 'slack',           label: 'Slack' },
//     ],
//   },
//   {
//     label: 'People',
//     views: [
//       { id: 'people',         label: 'People' },
//       { id: 'accounts',       label: 'Accounts' },
//       { id: 'recently_added', label: 'Recently Added' },
//       { id: 'no_email',       label: 'No Email' },
//     ],
//   },
// ];

const VIEW_SECTIONS: ViewSection[] = [
  {
    label: 'Relationship',
    views: [
      { id: 'all',          label: 'All Contacts' },
      { id: 'inner_circle', label: TIER_LABELS.inner_circle },
      { id: 'tier1',        label: TIER_LABELS.tier1 },
      { id: 'tier2',        label: TIER_LABELS.tier2 },
      { id: 'tier3',        label: TIER_LABELS.tier3 },
      { id: 'dormant',      label: TIER_LABELS.dormant },
    ],
  },
];

const VIEWS: ViewItem[] = VIEW_SECTIONS.flatMap((s) => s.views);

const VIEW_LABELS: Record<ContactsView, string> = Object.fromEntries(
  VIEWS.map((v) => [v.id, v.label])
) as Record<ContactsView, string>;

const VIEW_SHORT_LABELS: Record<ContactsView, string> = {
  all:            'All',
  inner_circle:   'Inner ●',
  tier1:          'Tier 1',
  tier2:          'Tier 2',
  tier3:          'Tier 3',
  dormant:        'Dormant',
  stale:          'Overdue',
  hot:            '🔥 Hot',
  reddit:         'Reddit',
  twitter:        'Twitter',
  slack:          'Slack',
  gmail:          'Gmail',
  calendar:       'Calendar',
  google_contacts:'G Contacts',
  linkedin:       'LinkedIn',
  people:         'People',
  accounts:       'Accounts',
  recently_added: 'Recent',
  no_email:       'No Email',
};

export const ContactsApp: React.FC<ContactsAppProps> = ({ windowId, spaceId, onClose }) => {
  const updateWindow = useSpaceStore((state) => state.updateWindow);
  usePageTitle('Contacts', undefined, 'contacts');

  const store = useContactsAppStore(windowId, spaceId);

  const isInitialized    = store((s) => s.isInitialized);
  const isLoading        = store((s) => s.isLoading);
  const error            = store((s) => s.error);
  const gmailConnected   = store((s) => s.gmailConnected);
  const currentView      = store((s) => s.currentView);
  const selectedContactId = store((s) => s.selectedContactId);
  const isFormOpen       = store((s) => s.isFormOpen);
  const editingContactId = store((s) => s.editingContactId);
  const isDiscovering    = store((s) => s.isDiscovering);
  const isImporting      = store((s) => s.isImporting);
  const importResult     = store((s) => s.importResult);
  const discoverResult   = store((s) => s.discoverResult);
  const filter           = store((s) => s.filter);
  const contacts         = store((s) => s.contacts);
  const getFilteredContacts = store((s) => s.getFilteredContacts);
  const filteredCount    = useMemo(
    () => getFilteredContacts().length,
    [contacts, currentView, filter, getFilteredContacts],
  );
  const syncLog          = store((s) => s.syncLog);
  const isSearchLoading  = store((s) => s.isSearchLoading);
  const searchResults    = store((s) => s.searchResults);
  const lastSearchQuery  = store((s) => s.lastSearchQuery);

  const initialize       = store((s) => s.initialize);
  const cleanup          = store((s) => s.cleanup);
  const setView          = store((s) => s.setView);
  const setFilter        = store((s) => s.setFilter);
  const openForm         = store((s) => s.openForm);
  const closeForm        = store((s) => s.closeForm);
  const selectContact    = store((s) => s.selectContact);
  const discoverContacts = store((s) => s.discoverContacts);
  const importFromCsv    = store((s) => s.importFromCsv);

  const isMobile = useDeviceDetection();
  const [mobilePanel, setMobilePanel] = useState<'list' | 'detail'>('list');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [lastSeenLogCount, setLastSeenLogCount] = useState(0);
  const unreadLogCount = syncLog.length - lastSeenLogCount;

  useEffect(() => {
    log.debug('[ContactsApp] isMobile=%s', isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && selectedContactId) setMobilePanel('detail');
  }, [isMobile, selectedContactId]);

  useEffect(() => {
    if (isMobile && !selectedContactId) setMobilePanel('list');
  }, [isMobile, selectedContactId]);

  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  const handleMobileBack = useCallback(() => {
    selectContact(null);
    setMobilePanel('list');
  }, [selectContact]);

  useEffect(() => {
    updateWindow(windowId, { title: 'Contacts' });
    if (!isInitialized) {
      initialize(windowId, spaceId);
    }
    return () => { cleanup(); };
  }, [windowId, spaceId]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleImportClick = useCallback(() => {
    setShowMoreMenu(false);
    setImportError(null);
    fileInputRef.current?.click();
  }, []);

  const handleOpenSync = useCallback(() => {
    setShowMoreMenu(false);
    setLastSeenLogCount(syncLog.length);
    setIsSyncModalOpen(true);
  }, [syncLog.length]);

  const handleDiscover = useCallback(async () => {
    await discoverContacts();
  }, [discoverContacts]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportError(null);
    try {
      await importFromCsv(file, file.name.toLowerCase().includes('linkedin') ? 'linkedin' : 'csv');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    }
  }, [importFromCsv]);

  if (isLoading && !isInitialized) {
    return (
      <div className="contacts-app contacts-app--loading">
        <div className="contacts-app__spinner" />
        <p>Loading contacts...</p>
      </div>
    );
  }

  if (error && !isInitialized) {
    return (
      <div className="contacts-app contacts-app--error">
        <AddressBook size={32} weight="duotone" />
        <p>{error}</p>
      </div>
    );
  }

  const selectedContact = contacts.find((c) => c._id === selectedContactId);

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".csv,text/csv"
      style={{ display: 'none' }}
      onChange={handleFileChange}
    />
  );

  const searchBar = (
    <div className="contacts-app__search">
      <MagnifyingGlass size={14} className="contacts-app__search-icon" />
      <input
        type="text"
        placeholder="Search name, email, notes, topics…"
        value={filter.search}
        onChange={(e) => setFilter({ search: e.target.value })}
        className="contacts-app__search-input"
      />
      {isSearchLoading && (
        <span className="contacts-app__search-spinner" aria-hidden />
      )}
      {filter.search && !isSearchLoading && (
        <button
          className="contacts-app__search-clear"
          onClick={() => setFilter({ search: '' })}
          title="Clear search"
          type="button"
        >
          <X size={10} weight="bold" />
        </button>
      )}
      {filter.search && (
        <div className="contacts-app__search-meta">
          {isSearchLoading
            ? 'Searching…'
            : searchResults !== null
              ? `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} for "${lastSearchQuery}"`
              : null}
        </div>
      )}
    </div>
  );

  // ── Mobile layout ──────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div className="contacts-app contacts-app--mobile">
        {gmailConnected === false && (
          <ContactsEmailWall onConnected={() => initialize(windowId, spaceId)} />
        )}

        <header className="contacts-app__mobile-header">
          {mobilePanel === 'list' ? (
            <>
              <div className="contacts-app__mobile-title">
                <AddressBook size={18} weight="duotone" />
                <span>Contacts</span>
              </div>
              <div className="contacts-app__mobile-actions">
                <div className="contacts-app__mobile-more-wrap" ref={moreMenuRef}>
                  <button
                    className="contacts-app__mobile-icon-btn"
                    onClick={() => setShowMoreMenu((v) => !v)}
                    title="More options"
                  >
                    <DotsThree size={20} weight="bold" />
                  </button>
                  {showMoreMenu && (
                    <div className="contacts-app__mobile-more-menu">
                      <button
                        className="contacts-app__mobile-more-item"
                        onClick={handleOpenSync}
                      >
                        <ArrowsCounterClockwise size={15} weight="bold" />
                        Sync &amp; Logs
                        {unreadLogCount > 0 && (
                          <span className="contacts-app__sync-badge">{unreadLogCount}</span>
                        )}
                      </button>
                      <button
                        className={`contacts-app__mobile-more-item${isDiscovering ? ' contacts-app__mobile-more-item--loading' : ''}`}
                        onClick={handleDiscover}
                        disabled={isDiscovering}
                      >
                        <UserPlus size={15} weight="bold" />
                        {isDiscovering ? 'Discovering…' : 'Discover Contacts'}
                      </button>
                      <button
                        className={`contacts-app__mobile-more-item${isImporting ? ' contacts-app__mobile-more-item--loading' : ''}`}
                        onClick={handleImportClick}
                        disabled={isImporting}
                      >
                        <ArrowsCounterClockwise size={15} weight="bold" />
                        {isImporting ? 'Importing…' : 'Import CSV'}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className="contacts-app__mobile-icon-btn contacts-app__mobile-icon-btn--primary"
                  onClick={() => openForm()}
                  title="New contact"
                >
                  <Plus size={18} weight="bold" />
                </button>
              </div>
            </>
          ) : (
            <>
              <button className="contacts-app__mobile-back-btn" onClick={handleMobileBack}>
                <ArrowLeft size={18} weight="bold" />
                <span>Back</span>
              </button>
              <span className="contacts-app__mobile-contact-name">
                {selectedContact?.name ?? ''}
              </span>
              <button
                className="contacts-app__mobile-icon-btn"
                onClick={() => selectedContactId && openForm(selectedContactId)}
                title="Edit contact"
              >
                <PencilSimple size={17} />
              </button>
            </>
          )}
        </header>

        {mobilePanel === 'list' && (
          <div className="contacts-app__mobile-view-chips">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                className={`contacts-app__mobile-chip${currentView === v.id ? ' contacts-app__mobile-chip--active' : ''}`}
                onClick={() => setView(v.id)}
              >
                {VIEW_SHORT_LABELS[v.id]}
              </button>
            ))}
          </div>
        )}

        {importResult && !isImporting && mobilePanel === 'list' && (
          <div className="contacts-app__import-result contacts-app__import-result--mobile">
            <CheckCircle size={12} weight="fill" />
            {importResult.created > 0 && <span>{importResult.created} added</span>}
            {importResult.updated > 0 && <span>{importResult.updated} updated</span>}
            {importResult.skipped > 0 && <span>{importResult.skipped} skipped</span>}
          </div>
        )}
        {importError && mobilePanel === 'list' && (
          <div className="contacts-app__import-error contacts-app__import-error--mobile">{importError}</div>
        )}

        <div className={`contacts-app__list-panel${mobilePanel !== 'list' ? ' contacts-app__panel--hidden' : ''}`}>
          {searchBar}
          <ContactList windowId={windowId} spaceId={spaceId} />
        </div>

        <div className={`contacts-app__detail-panel${mobilePanel !== 'detail' ? ' contacts-app__panel--hidden' : ''}`}>
          {selectedContactId ? (
            <ContactDetail windowId={windowId} spaceId={spaceId} />
          ) : (
            <div className="contacts-app__empty-detail">
              <AddressBook size={40} weight="duotone" />
              <p>Select a contact to view details</p>
            </div>
          )}
        </div>

        {fileInput}

        {isFormOpen && (
          <ContactForm
            windowId={windowId}
            spaceId={spaceId}
            contactId={editingContactId ?? undefined}
            onClose={closeForm}
          />
        )}

        {isSyncModalOpen && (
          <SyncModal
            windowId={windowId}
            spaceId={spaceId}
            onClose={() => setIsSyncModalOpen(false)}
            onImportClick={handleImportClick}
          />
        )}
      </div>
    );
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────

  return (
    <div className="contacts-app">
      {gmailConnected === false && (
        <ContactsEmailWall onConnected={() => initialize(windowId, spaceId)} />
      )}

      {/* Sidebar */}
      <aside className="contacts-app__sidebar">
        <div className="contacts-app__sidebar-header">
          <AddressBook size={20} weight="duotone" />
          <span>Contacts</span>
        </div>

        <nav className="contacts-app__nav">
          {VIEW_SECTIONS.map((section) => (
            <div key={section.label} className="contacts-app__nav-section">
              <span className="contacts-app__nav-section-label">{section.label}</span>
              {section.views.map((v) => (
                <button
                  key={v.id}
                  className={`contacts-app__nav-item${currentView === v.id ? ' contacts-app__nav-item--active' : ''}`}
                  onClick={() => setView(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="contacts-app__sidebar-actions">
          <button
            className="contacts-app__action-btn contacts-app__action-btn--primary"
            onClick={() => openForm()}
          >
            <Plus size={14} weight="bold" />
            New Contact
          </button>
          <button
            className={`contacts-app__action-btn contacts-app__action-btn--sync${isDiscovering ? ' contacts-app__action-btn--loading' : ''}`}
            onClick={handleDiscover}
            disabled={isDiscovering}
            title="Auto-discover contacts from Gmail and Calendar"
          >
            <UserPlus size={14} weight="bold" />
            {isDiscovering ? 'Discovering…' : 'Discover'}
          </button>
          <button
            className="contacts-app__action-btn contacts-app__action-btn--sync"
            onClick={handleOpenSync}
            title="Sync Center — view logs and import contacts"
          >
            <ArrowsCounterClockwise size={14} weight="bold" />
            Sync
            {unreadLogCount > 0 && (
              <span className="contacts-app__sync-badge">{unreadLogCount}</span>
            )}
          </button>
          {discoverResult && !isDiscovering && (
            <div className="contacts-app__import-result">
              <CheckCircle size={12} weight="fill" />
              {discoverResult.discovered > 0
                ? `${discoverResult.discovered} new contact${discoverResult.discovered !== 1 ? 's' : ''} found`
                : 'No new contacts found'}
            </div>
          )}
          {importResult && !isImporting && (
            <div className="contacts-app__import-result">
              <CheckCircle size={12} weight="fill" />
              {[
                importResult.created  > 0 ? `${importResult.created} added`   : null,
                importResult.updated  > 0 ? `${importResult.updated} updated`  : null,
                importResult.skipped  > 0 ? `${importResult.skipped} skipped`  : null,
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {importError && (
            <div className="contacts-app__import-error">{importError}</div>
          )}
        </div>
      </aside>

      {/* Contact list */}
      <div className={`contacts-app__list-panel${!selectedContactId ? ' contacts-app__list-panel--expanded' : ''}`}>
        <div className="contacts-app__list-inner">
          {!selectedContactId && (
            <div className="contacts-app__main-header">
              <div className="contacts-app__main-header-left">
                <h1 className="contacts-app__main-title">{VIEW_LABELS[currentView]}</h1>
                <span className="contacts-app__main-count">{filteredCount}</span>
              </div>
              <div className="contacts-app__main-header-actions">
                <button
                  className="contacts-app__action-btn contacts-app__action-btn--sync"
                  onClick={handleOpenSync}
                  title="Sync Center — view logs and import contacts"
                >
                  <ArrowsCounterClockwise size={14} weight="bold" />
                  Sync
                  {unreadLogCount > 0 && (
                    <span className="contacts-app__sync-badge">{unreadLogCount}</span>
                  )}
                </button>
                <button
                  className="contacts-app__action-btn contacts-app__action-btn--primary"
                  onClick={() => openForm()}
                >
                  <Plus size={14} weight="bold" />
                  New Contact
                </button>
              </div>
            </div>
          )}
          {searchBar}
          <ContactList windowId={windowId} spaceId={spaceId} />
        </div>
      </div>

      {/* Detail panel */}
      {selectedContactId && (
        <div className="contacts-app__detail-panel">
          <ContactDetail windowId={windowId} spaceId={spaceId} />
        </div>
      )}

      {fileInput}

      {isFormOpen && (
        <ContactForm
          windowId={windowId}
          spaceId={spaceId}
          contactId={editingContactId ?? undefined}
          onClose={closeForm}
        />
      )}

      {isSyncModalOpen && (
        <SyncModal
          windowId={windowId}
          spaceId={spaceId}
          onClose={() => setIsSyncModalOpen(false)}
          onImportClick={handleImportClick}
        />
      )}
    </div>
  );
};
